// M1 · Me tab (QM1_Me): account, your buddy, settings (plan time on the XTime wheel, check-ins, reminders, quiet
// hours; Start at login on desktop), membership, the test tools (POST /api/debug/:tool), sign out, delete account.
import { useEffect, useState } from 'preact/hooks';
import type { CheckIn } from '@shared/types';
import { C } from '@shared/content/characters';
import { AI_NOTE, fill } from '@shared/content/lines';
import { fmtTime, localMinutes } from '@shared/rules';
import { useApp, appStore, updateSettings, enableReminders, debugTool, signOut, deleteAccount, toast } from '../../store';
import { go } from '../../router';
import { desktop } from '../../native';
import type { DebugBody, DebugTool } from '../../api/types';
import { Screen, ScrollArea, XFace, XTime, BottomSheet, PrimaryButton, TextButton, Icon } from '../../components';
import { ConfirmSheet } from './ConfirmSheet';
import { TimeZoneSheet, tzValue } from './TimeZoneSheet';
import { ME } from './copy';
import s from './me.module.css';

const CHECKINS: CheckIn[] = ['light', 'steady', 'close'];
const cap = (x: string) => x.charAt(0).toUpperCase() + x.slice(1);
const POINT_CHIPS = [50, 100, 250, 500];
const latest = () => appStore.get().state;

export function MeScreen() {
  const st = useApp((x) => x.state);
  const [sheet, setSheet] = useState<'time' | 'tz' | 'delete' | 'restart' | null>(null);
  const [draft, setDraft] = useState('9:00 AM');
  const [busy, setBusy] = useState<string | null>(null);
  const [login, setLogin] = useState<boolean | null>(null);
  useEffect(() => { if (desktop.available()) void desktop.getLoginItem().then(setLogin); }, []);
  if (!st) return <Screen theme="margo" tab="me" id="M1" />;
  const ch = st.ch, c = C[ch], u = st.user;
  const todo = st.plan.cards.filter((x) => x.st === 'todo').length;

  const tool = async (key: string, t: DebugTool, body: DebugBody = {}, then?: () => void) => {
    if (busy) return;
    setBusy(key);
    const r = await debugTool(t, body);
    setBusy(null);
    if (r && then) then();
  };
  const tools: { key: string; label: string; run: () => void }[] = [
    { key: 'morning', label: 'Morning reminder', run: () => tool('morning', 'morning', {}, () => go('C')) },
    { key: 'nudge', label: 'No reply', run: () => tool('nudge', 'nudge', {}, () => go('C')) },
    { key: 'away2', label: 'Away 2 days', run: () => tool('away2', 'away2', {}, () => go('C')) },
    { key: 'chatonly', label: 'Chat, no card', run: () => tool('chatonly', 'chatonly', {}, () => go('C')) },
    { key: 'evening', label: 'Evening wrap', run: () => tool('evening', 'evening', {}, () => go('C')) },
    { key: 'crisis', label: 'Crisis words in chat', run: () => tool('crisis', 'crisis', {}, () => go('M6')) },
    { key: 'nextday', label: 'Next day', run: () => tool('nextday', 'nextday', {}, () => go('C')) },
    { key: 'week', label: 'Week recap now', run: () => tool('week', 'week', {}, () => go('U3')) },
    {
      key: 'advance', label: 'One hour later', run: () => tool('advance', 'advance', { minutes: 60 }, () => {
        const s2 = latest();
        if (s2) toast(`It's now ${fmtTime(localMinutes(Date.parse(s2.now), s2.user.tz))}.`);
      }),
    },
    { key: 'restart', label: 'Restart onboarding', run: () => setSheet('restart') },
  ];
  const addPoints = (n: number) => tool('p' + n, 'points', { add: n }, () => {
    const s2 = latest();
    if (s2) toast(`+${n} points · Level ${s2.level.level}`, { action: { label: 'See Us', run: () => go('U1') } });
  });

  const saveTime = async () => { setSheet(null); if (draft !== u.planTime) await updateSettings({ planTime: draft }); };
  const cycle = () => { const i = CHECKINS.indexOf(u.checkin); void updateSettings({ checkin: CHECKINS[(i + 1) % CHECKINS.length] }); };
  const reminders = async () => { const on = !u.reminders; const r = await updateSettings({ reminders: on }); if (r && on) void enableReminders(); };
  const startAtLogin = async () => { const on = !login; await desktop.setLoginItem(on); setLogin(await desktop.getLoginItem()); };
  const remove = async () => { setBusy('delete'); const ok = await deleteAccount(); setBusy(null); if (!ok) setSheet(null); };
  const restart = async () => { setBusy('restart'); await debugTool('restart'); setBusy(null); setSheet(null); };

  return (
    <Screen theme={ch} tab="me" badge={todo} id="M1">
      <h1 class={s.h1}>Me</h1>
      <ScrollArea class={s.meScroll}>
        <div class={s.list}>
          <section class={`${s.card} ${s.acct}`} aria-label="Account">
            <span class={s.initial} aria-hidden="true">{(u.email.charAt(0) || 'Y').toUpperCase()}</span>
            <span class={s.two}><span class={s.email}>{u.email}</span><span class={s.small}>{`Invite ${u.invite} · Day ${st.dayIndex}`}</span></span>
          </section>

          <button type="button" class={`${s.card} ${s.buddy}`} onClick={() => go('M2')}>
            <XFace ch={ch} e={1} s={44} ring="var(--acc)" alt="" />
            <span class={s.two} style={{ flex: 1 }}>
              <span class={s.kicker}>YOUR BUDDY</span>
              <span class={s.buddyName}>{c.name}</span>
              <span class={s.small}>{ME.buddySub(c.role, c.pron)}</span>
            </span>
            <Icon name="chevron" size={18} class={s.chev} />
          </button>

          <section class={`${s.card} ${s.prefs}`} aria-label="Settings">
            <button type="button" class={s.pref} aria-haspopup="dialog" onClick={() => { setDraft(u.planTime); setSheet('time'); }}>
              <span class={s.prefL}>We plan at</span><span class={s.prefV}>{u.planTime}</span>
            </button>
            <button type="button" class={s.pref} onClick={cycle} aria-label={`Check-ins: ${cap(u.checkin)}. Tap to change.`}>
              <span class={s.prefL}>Check-ins</span><span class={s.prefV}>{cap(u.checkin)}</span>
            </button>
            <button type="button" class={s.pref} aria-haspopup="dialog" onClick={() => setSheet('tz')} aria-label={`Time zone: ${tzValue(u.tz, u.tzAuto)}. New.`}>
              <span class={s.prefL}>Time zone<span class={s.newTag} aria-hidden="true">NEW</span></span><span class={s.prefV}>{tzValue(u.tz, u.tzAuto)}</span>
            </button>
            <button type="button" class={s.pref} role="switch" aria-checked={u.reminders ? 'true' : 'false'} onClick={() => void reminders()}>
              <span class={s.prefL}>{ME.reminders}</span><span class={s.toggle} data-on={u.reminders ? 'true' : 'false'} aria-hidden="true"><span /></span>
            </button>
            {login != null && (
              <button type="button" class={s.pref} role="switch" aria-checked={login ? 'true' : 'false'} onClick={() => void startAtLogin()}>
                <span class={s.prefL}>{ME.startAtLogin}</span><span class={s.toggle} data-on={login ? 'true' : 'false'} aria-hidden="true"><span /></span>
              </button>
            )}
            <p class={s.quiet}>{ME.quiet}</p>
          </section>

          <section class={`${s.card} ${s.plan}`} aria-label="Membership">
            <div class={s.planRow}>
              <span class={s.two}>
                <span class={s.planName}>{u.vip ? 'VIP (simulated)' : 'Free'}</span>
                <span class={s.small}>{u.vip ? 'Talk as much as you want' : `${st.usage.freeLimit} messages a day`}</span>
              </span>
              <button type="button" class={s.vipBtn} onClick={() => go('M4')}>{u.vip ? 'Manage' : 'Try VIP'}</button>
            </div>
            <button type="button" class={s.friendsRow} onClick={() => go('M5')}><span>Friends (demo)</span><Icon name="chevron" size={18} class={s.chev} /></button>
          </section>

          <p class={s.ai}>{fill(AI_NOTE, { n: c.name })}</p>

          <section class={`${s.card} ${s.tools}`} aria-label="Test tools">
            <p class={s.toolsCap}>{ME.tools}</p>
            <div class={s.toolGrid}>
              {tools.map((t) => (
                <button type="button" class={s.tool} key={t.key} aria-busy={busy === t.key ? 'true' : undefined} onClick={() => t.run()}>{t.label}</button>
              ))}
            </div>
            <p class={s.toolSub}>{ME.addPoints}</p>
            <div class={s.chips}>
              {POINT_CHIPS.map((n) => (
                <button type="button" class={s.tool} key={n} aria-label={`Add ${n} points`} aria-busy={busy === 'p' + n ? 'true' : undefined} onClick={() => void addPoints(n)}>{`+${n}`}</button>
              ))}
            </div>
            <p class={s.model}>{ME.model(st.usage.costTotalUsd)}</p>
          </section>

          <div class={s.foot}>
            <TextButton onClick={() => void signOut()}>Sign out</TextButton>
            <TextButton tone="danger" onClick={() => setSheet('delete')}>Delete account</TextButton>
          </div>
        </div>
      </ScrollArea>

      <BottomSheet open={sheet === 'time'} onClose={() => setSheet(null)} label={ME.planSheet} style={{ alignItems: 'center' }}>
        <p class={s.sheetT} style={{ alignSelf: 'stretch' }}>{ME.planSheet}</p>
        <p class={s.sheetSub} style={{ alignSelf: 'stretch' }}>{ME.planSheetSub(c.name)}</p>
        <XTime value={draft} onPick={setDraft} bg="#1C1916" w={342} />
        <PrimaryButton h={56} data-cta="done" style={{ alignSelf: 'stretch' }} onClick={() => void saveTime()}>Done</PrimaryButton>
      </BottomSheet>
      <TimeZoneSheet open={sheet === 'tz'} tz={u.tz} auto={u.tzAuto} onClose={() => setSheet(null)} />
      <ConfirmSheet open={sheet === 'delete'} title={ME.deleteTitle} body={ME.deleteBody} confirm="Delete account" busy={busy === 'delete'} onConfirm={() => void remove()} onClose={() => setSheet(null)} />
      <ConfirmSheet open={sheet === 'restart'} title={ME.restartTitle} body={ME.restartBody} confirm="Restart" busy={busy === 'restart'} onConfirm={() => void restart()} onClose={() => setSheet(null)} />
    </Screen>
  );
}

